import { ChangeDetectionStrategy, Component, computed, ElementRef, OnDestroy, OnInit, signal, ViewChild, ViewEncapsulation, WritableSignal } from '@angular/core';
import { ImageClasses, SPIN_APPEND, SPIN_STYLES, SpinInputModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { finalize, of, Subscription } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { FileService } from '../../../services/file-service';
import { SharedService } from '../../shared/shared.service';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { ResizeModel } from 'src/app/model/eng/resize.model';
import { CommonModule } from '@angular/common';
import { SpinDirective } from 'src/app/directives/spin.directive';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {MatTabsModule} from '@angular/material/tabs';
import { HeadersComponent } from './headers/headers.component';
import { FootersComponent } from './footers/footers.component';
import { SignaturesComponent } from './signatures/signatures.component';
import { ResizableDrawerDirective } from 'src/app/directives/resizable.directive';

@Component({
  selector: 'app-components',
  templateUrl: './components.component.html',
  styleUrls: ['./components.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatSidenavModule, SpinDirective, MatButtonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatTabsModule, HeadersComponent, FootersComponent, SignaturesComponent, ResizableDrawerDirective
  ],
  standalone: true
})
export class ComponentsComponent implements OnInit, OnDestroy {
  imageFile: any;
  tabIndex: WritableSignal<number> = signal<number>(0);
  loggedInUserData: any;
  firmuseracctid: any;
  s3signatureKey: any;
  headersData: WritableSignal<any[]> = signal<any[]>([]);
  footersData: WritableSignal<any[]> = signal<any[]>([]);
  selectedHeader: any;
  selectedFooter: any;
  componentHeaderSub: Subscription;
  componentFooterSub: Subscription;
  deleteFooterOrHeaderSub: Subscription;
  isDrawerOpened = false;

  @ViewChild('drawer', { static: false }) drawer!: MatDrawer;
  @ViewChild(HeadersComponent) headersComponent!: HeadersComponent;
  @ViewChild(FootersComponent) footersComponent!: FootersComponent;
  @ViewChild(SignaturesComponent) signaturesComponent!: SignaturesComponent;
  searchTerm: WritableSignal<string> = signal<string>('');
  @ViewChild('searchBox') searchBox!: ElementRef<HTMLInputElement>;

  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(SPIN_APPEND.HOST, SPIN_STYLES.BLUR_WITH_SPIN)); 
  private spinCount: number = 0; 

  isHeaderTab = computed(() => this.tabIndex() === 0);
  isFooterTab = computed(() => this.tabIndex() === 1);
  isSignatureTab = computed(() => this.tabIndex() === 2);

  hasHeaders = computed(() => this.headersData().length > 0);
  hasFooters = computed(() => this.footersData().length > 0);

  showHeaderFooterPreview = computed(() =>
    (this.isHeaderTab() || this.isFooterTab()) && this.hasHeaders() && this.hasFooters()
  );

  showEmptyHeader = computed(() =>
    this.isHeaderTab() && !this.hasHeaders()
  );

  showHeaderOnly = computed(() =>
    this.isHeaderTab() && this.hasHeaders() && !this.hasFooters()
  );

  showEmptyFooter = computed(() =>
    this.isFooterTab() && !this.hasFooters()
  );

  showFooterOnly = computed(() =>
    this.isFooterTab() && this.hasFooters() && !this.hasHeaders()
  );

  previewToggleVisible = computed(() =>
    (this.tabIndex() === 0 || this.tabIndex() === 1) &&
    (this.headersData().length > 0 || this.footersData().length > 0)
  );
  showDrawer = computed(() => this.tabIndex() !== 2);

  constructor(
              private toastr: ToastrService,
              private shareService: SharedService,
              private apiService: ApiService,
              private fileService: FileService) { }

  ngOnInit() {
    this.loggedInUserData = JSON.parse((localStorage.getItem('userDetails')));
    if (this.loggedInUserData && this.loggedInUserData.firmUserAcctId) {
      this.firmuseracctid = this.loggedInUserData.firmUserAcctId
    }
    this.s3signatureKey = 'Engagements/' + this.firmuseracctid + '_Firm/' + 'Templates/' + 'Common/' + 'Headers';
    this.getHeadersFooters();
    this.componentHeaderSub = this.shareService.changeHeaderSelection.subscribe(res => {
      this.selectedHeader = res;
      this.isDrawerOpened = true;
      this.toggleDrawer();
    });

    this.componentFooterSub = this.shareService.changeFooterSelection.subscribe(res => {
      this.selectedFooter = res;
      this.isDrawerOpened = true;
      this.toggleDrawer();
    });
    this.deleteFooterOrHeaderSub = this.shareService.refreshHeaderFooter.subscribe(res => {
      if(res){
        this.getHeadersFooters();
      }
    });
  }

  toggleDrawer() {
    if(this.drawer) {
      if (!this.drawer.opened) {
        this.drawer.toggle();
      }
    }
  }

  onChangeTab(tabindex: number): void {
    this.tabIndex.set(tabindex);
    this.searchTerm.set('');
    this.searchBox.nativeElement.value = '';
    if(tabindex === 2){
      this.isDrawerOpened = false;
    }
  }

  getHeadersFooters() {
    this.headersData.set([]);
    this.footersData.set([]);
    this.updateSpinnerConfig(true);

    this.fileService.getFirmHeaderAndFooter().pipe(
      finalize(() => this.updateSpinnerConfig(false))
    ).subscribe({
      next: (res) => {
        if (res.status !== 200) {
          this.toastr.error('Failed to fetch headers and footers');
          return;
        }

        const result = JSON.parse(res.data);

        // Process headers
        if (result.headerdetails?.length > 0) {
          const headersData = result.headerdetails.map(header => ({
            ...header,
            s3JSONFilePath$: this.apiService.getFiles(header.JsonFilePath),
            resized_JsonFilePath$: header.resized_JsonFilePath
              ? this.apiService.getFiles(header.resized_JsonFilePath)
              : of(null)
          }));
          this.headersData.set(headersData);
          this.selectedHeader = headersData.find(h => h.isuserselected === 1) || headersData[0];
        } else {
          this.selectedHeader = null;
        }

        // Process footers
        if (result.footerdetails?.length > 0) {
          const footersData = result.footerdetails.map(footer => ({
            ...footer,
            s3JSONFilePath$: this.apiService.getFiles(footer.JsonFilePath),
            resized_JsonFilePath$: footer.resized_JsonFilePath
              ? this.apiService.getFiles(footer.resized_JsonFilePath)
              : of(null)
          }));
          this.footersData.set(footersData);
          this.selectedFooter = footersData.find(f => f.isuserselected === 1) || footersData[0];
        } else {
          this.selectedFooter = null;
        }
      },
      error: (error) => {
        this.toastr.error(error.message || 'Failed to fetch headers and footers');
      }
    });
  }

  ngOnDestroy() {
    if(this.componentFooterSub){
      this.componentFooterSub.unsubscribe();
    }
    if(this.componentHeaderSub){
      this.componentHeaderSub.unsubscribe();
    }
    if(this.deleteFooterOrHeaderSub){
      this.deleteFooterOrHeaderSub.unsubscribe();
    }
  }
  getImageClassHeader() {
    const classes = ['d-flex', 'flex-row', 'bg-transparent'];

    if (this.selectedHeader?.is_stretch_fit === 1) {
      classes.push(ImageClasses.stretchFitHeader);
    } else {
      const alignment = this.selectedHeader?.picture_alignment;
      if (alignment === 'center') {
        classes.push(ImageClasses.center);
      } else if (alignment === 'left') {
        classes.push(ImageClasses.left);
      } else if (alignment === 'right') {
        classes.push(ImageClasses.right);
      }
    }

    return classes.join(' ');
  }

  getImageClassFooter() {
    const classes = ['d-flex', 'flex-row', 'bg-transparent'];

    if (this.selectedFooter?.is_stretch_fit === 1) {
      classes.push(ImageClasses.stretchFitFooter);
    } else {
      const alignment = this.selectedFooter?.picture_alignment;
      if (alignment === 'center') {
        classes.push(ImageClasses.center);
      } else if (alignment === 'left') {
        classes.push(ImageClasses.left);
      } else if (alignment === 'right') {
        classes.push(ImageClasses.right);
      }
    }

    return classes.join(' ');
  }
  adjustHeight(event: any){
    const imgHeight = event.target.height;
    // console.log(`Image Dimensions: Width - ${event.target.width} x Height - ${imgHeight}`);
    const pageHeader = document.querySelector('.page-header') as HTMLElement;
    if (pageHeader) {
      pageHeader.style.cssText = `height: auto !important; min-height: ${imgHeight}px !important; padding-bottom: 20px; padding-top: 10px; max-height: 200px !important;`;
    }
  }
  checkDimensions(event: any){
    // console.log(`Image Dimensions: Width - ${event.target.width} x Height - ${event.target.height}`);
  }

  public onResizePanelEnd(resizeData: ResizeModel) {
    resizeData.width < resizeData.minWidth ? this.drawer.close() : '';
    this.isDrawerOpened = false;
  }

  public updateSpinnerConfig(value: boolean) { 
      value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1)); 
      const spinner = this.spinnerConfig(); 
      this.spinnerConfig.set({ 
       ...spinner,  
         isSpinShown: this.spinCount > 0 
       });  
     }

  onAddClick() {
    if (this.tabIndex() === 0) {
      this.headersComponent.newHeader.set(true);
    } else if (this.tabIndex() === 1) {
      this.footersComponent.newFooter.set(true);
    } else {
      this.signaturesComponent.addSignature();
    }
  }

  onSearch(text: string) {
    this.searchTerm.set(text);
    console.log("Search Term:", this.searchTerm());
  }  
}
